import React, { useEffect, useMemo, useState } from 'react'
import { ChevronRight, Download, FileText, Trash2 } from 'lucide-react'
import Button from '../../ui/Button'
import MultiSelect from '../../ui/MultiSelect'
import type { ReportListItem } from '../../../utils/reportsClient'
import type { PqrWeldOption } from '../pqrPreview/PqrWeldPickerModal'
import {
  groupReportsForHistory,
  AGGREGATED_GROUP_ID,
  type PqrHistoryReport
} from './groupReportsForHistory'
import { formatPqrDateTime } from '../../../utils/formatPqrDateTime'
import ReportVersionsModal from '../ReportVersionsModal'
import PageLoader from '../../ui/PageLoader'

/**
 * PQR report history rendered as a CRCE-style flat card grid.
 *
 * Each card represents one PQR No. train. The card face shows the
 * **next** revision to create (synthetic placeholder); Download / Remove
 * target the latest **real** generated PDF. Card click opens the
 * editable preview seeded at the next revision. Trains with zero real
 * PDFs are omitted — the header Generate PQR button is the entry-point
 * for fresh scopes.
 */
export interface PqrReportHistoryProps {
  reports: ReportListItem[]
  weldOptions: PqrWeldOption[]
  onDownload: (report: ReportListItem) => void
  onRemove: (report: ReportListItem, meta: { isLatest: boolean }) => void
  /**
   * Card body click handler. Receives the synthetic next-version row
   * so the caller can navigate with `?new=1&pqrNumber=…&revision=…`.
   * In importMode, opens regenerated PDF View instead.
   */
  onPreview?: (report: PqrHistoryReport) => void
  busyId?: string | null
  /** When true, shows a loader instead of the empty-state card. */
  listLoading?: boolean
  /** PDF-import pipe set: no Generate entry-point messaging. */
  importMode?: boolean
}

const formatTimestamp = formatPqrDateTime

interface TrainEntry {
  scopeId: string
  scopeLabel: string
  scopeWeldIds: string[]
  scopeChips: string[]
  number: string
  /** Synthetic next-version row — card click / display. */
  previewTarget: PqrHistoryReport
  /** Highest real revision — Download / Remove. */
  downloadTarget: PqrHistoryReport
  older: PqrHistoryReport[]
  versionCount: number
}

interface FilterOption {
  id: string
  label: string
}

function asRevNumber(rev: string | undefined): number {
  if (typeof rev !== 'string') return 0
  const t = rev.trim()
  if (!/^\d+$/.test(t)) return 0
  return parseInt(t, 10)
}

function maxRevNumber(items: ReportListItem[]): number {
  let max = -1
  for (const r of items) {
    const n = asRevNumber(r.revision)
    if (n > max) max = n
  }
  return max
}

/** Fallback when grouping did not prepend a synthetic (aggregated bucket). */
function buildSyntheticPreview(
  scopeWeldIds: string[],
  pqrNumber: string,
  maxRev: number
): PqrHistoryReport {
  const trainKey = scopeWeldIds.length > 0 ? scopeWeldIds.join('|') : pqrNumber
  return {
    id: `synth-${trainKey}-${pqrNumber}`,
    name: pqrNumber,
    tag: 'PQR',
    fileKey: '',
    uploadedAt: '',
    weldId: scopeWeldIds[0] ?? '',
    weldIds: scopeWeldIds,
    pqrNumber,
    revision: String(maxRev + 1),
    isLatest: true,
    isSynthetic: true
  }
}

function RevPill({ revision }: { revision: string | undefined }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[rgba(0,39,63,0.06)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blueprint-blue">
      v{typeof revision === 'string' && revision.length > 0 ? revision : '0'}
    </span>
  )
}

function NextTag() {
  return (
    <span className="inline-flex items-center rounded-full bg-[rgba(0,39,63,0.06)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blueprint-blue">
      Next
    </span>
  )
}

function ChipTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded bg-[rgba(0,39,63,0.06)] px-1.5 py-0.5 text-[10px] font-medium text-blueprint-blue">
      {children}
    </span>
  )
}

interface TrainCardProps {
  train: TrainEntry
  onDownload: PqrReportHistoryProps['onDownload']
  onRemove: PqrReportHistoryProps['onRemove']
  onPreview?: PqrReportHistoryProps['onPreview']
  onShowVersions: (train: TrainEntry) => void
  busyId?: string | null
  importMode?: boolean
}

function TrainCard({
  train,
  onDownload,
  onRemove,
  onPreview,
  onShowVersions,
  busyId,
  importMode
}: TrainCardProps) {
  const { previewTarget, downloadTarget, older } = train
  const cardBusy =
    busyId != null &&
    (busyId === downloadTarget.id || busyId === previewTarget.id)
  const cardActsAsPreview = !!onPreview && !cardBusy
  const downloadBusy = cardBusy
  const viewLabel = importMode ? 'View' : 'preview'

  const stop =
    (fn: () => void): React.MouseEventHandler<HTMLButtonElement> =>
    (e) => {
      e.stopPropagation()
      fn()
    }

  const previewPayload = (): PqrHistoryReport => {
    const target = importMode ? downloadTarget : previewTarget
    const weldIds =
      Array.isArray(target.weldIds) && target.weldIds.length > 0
        ? target.weldIds
        : train.scopeWeldIds
    return weldIds.length > 0 ? { ...target, weldIds } : target
  }

  const handleCardClick = () => {
    if (!cardActsAsPreview || cardBusy) return
    onPreview?.(previewPayload())
  }
  const handleCardKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!cardActsAsPreview || cardBusy) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onPreview?.(previewPayload())
    }
  }

  return (
    <div
      data-testid={`pqr-history-card-${train.scopeId}-${train.number}`}
      role={cardActsAsPreview ? 'button' : undefined}
      tabIndex={cardActsAsPreview ? 0 : undefined}
      aria-busy={cardBusy || undefined}
      aria-disabled={cardBusy || undefined}
      aria-label={
        cardActsAsPreview ? `Open ${viewLabel} for ${train.number}` : undefined
      }
      onClick={cardActsAsPreview ? handleCardClick : undefined}
      onKeyDown={cardActsAsPreview ? handleCardKey : undefined}
      className={`relative overflow-hidden rounded-xl border border-[#E2E5EA] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blueprint-blue/40 ${
        cardBusy
          ? 'pointer-events-none cursor-wait opacity-60'
          : 'cursor-pointer hover:-translate-y-[2px] hover:shadow-[0_8px_24px_rgba(0,39,63,0.08)]'
      }`}
    >
      <div className="absolute left-0 right-0 top-0 h-[3px] bg-hero-red" />

      <div className="p-5 pt-6">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 ">
              <h3 className="truncate font-heading text-[14px] font-semibold text-blueprint-blue">
                {train.number}
              </h3>
              {train.scopeChips.length > 1 ? (
                train.scopeChips.map((chip) => (
                  <ChipTag key={chip}>{chip}</ChipTag>
                ))
              ) : (
                <ChipTag>{train.scopeLabel}</ChipTag>
              )}
            </div>
            <p className="mt-2 flex items-center gap-1.5 truncate text-[11px] text-heritage-grey">
              <FileText className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{downloadTarget.name}</span>
            </p>
          </div>
          <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-heritage-grey" />
        </div>

        <p className="mt-1 text-[11px] text-heritage-grey">
          {formatPqrDateTime(downloadTarget.uploadedAt)}
        </p>

        {/* <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <RevPill revision={previewTarget.revision} />
          <NextTag />
          <ChipTag>
            {train.versionCount} version{train.versionCount === 1 ? '' : 's'}
          </ChipTag>
        </div> */}

        <div className="mt-4 flex items-center justify-between gap-2 border-t border-[#EEF0F3] pt-3">
          {!importMode && older.length > 0 ? (
            <button
              type="button"
              className="text-[11px] font-medium text-blueprint-blue underline-offset-2 hover:underline"
              onClick={stop(() => onShowVersions(train))}
              data-testid={`pqr-history-versions-${train.scopeId}-${train.number}`}
            >
              Show all {train.versionCount} versions
            </button>
          ) : (
            <span aria-hidden />
          )}
          <div className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              className="h-8 px-2 text-[11px]"
              disabled={downloadBusy}
              onClick={stop(() => onRemove(downloadTarget, { isLatest: true }))}
              data-testid={`pqr-history-remove-${downloadTarget.id}`}
              title="Remove latest version"
              aria-label="Remove latest version"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="primary"
              className="h-8 px-2 text-[11px]"
              disabled={downloadBusy}
              onClick={stop(() => onDownload(downloadTarget))}
              data-testid={`pqr-history-download-${downloadTarget.id}`}
              title="Download latest version"
            >
              <Download className=" h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function buildTrains(
  grouped: ReturnType<typeof groupReportsForHistory>,
  weldNumberByWeldId: Map<string, string>
): { trains: TrainEntry[]; filterOptions: FilterOption[] } {
  const trains: TrainEntry[] = []
  const filterOptions: FilterOption[] = []
  const seenScopes = new Set<string>()

  const pushTrain = (
    scopeId: string,
    scopeLabel: string,
    scopeWeldIds: string[],
    scopeChips: string[],
    number: string,
    reports: PqrHistoryReport[]
  ) => {
    const real = reports.filter((r) => !r.isSynthetic)
    if (real.length === 0) return
    const previewTarget =
      reports.find((r) => r.isSynthetic === true) ??
      buildSyntheticPreview(scopeWeldIds, number, maxRevNumber(real))
    if (!seenScopes.has(scopeId)) {
      seenScopes.add(scopeId)
      filterOptions.push({ id: scopeId, label: scopeLabel })
    }
    trains.push({
      scopeId,
      scopeLabel,
      scopeWeldIds,
      scopeChips,
      number,
      previewTarget,
      downloadTarget: real[0],
      older: real.slice(1),
      versionCount: real.length
    })
  }

  const chipFor = (_weldId: string, weldNumber: string): string =>
    weldNumber.trim() || '\u2014'

  for (const wg of grouped.weldGroups) {
    const chips = wg.weldIds.map((wid, i) => {
      const wn = wg.weldNumbers[i] ?? weldNumberByWeldId.get(wid) ?? ''
      return chipFor(wid, wn)
    })
    const label =
      chips.length === 1
        ? `Weld ${wg.weldNumber?.trim() || '\u2014'}`
        : chips.join(', ')
    for (const pg of wg.pqrGroups) {
      pushTrain(wg.id, label, wg.weldIds, chips, pg.pqrNumber, pg.reports)
    }
  }
  if (grouped.aggregated) {
    for (const pg of grouped.aggregated.pqrGroups) {
      pushTrain(
        AGGREGATED_GROUP_ID,
        'Aggregated',
        [],
        [],
        pg.pqrNumber,
        pg.reports
      )
    }
  }
  return { trains, filterOptions }
}

export default function PqrReportHistory({
  reports,
  weldOptions,
  onDownload,
  onRemove,
  onPreview,
  busyId,
  listLoading,
  importMode
}: PqrReportHistoryProps) {
  const grouped = useMemo(
    () => groupReportsForHistory({ reports, weldOptions }),
    [reports, weldOptions]
  )
  const weldNumberByWeldId = useMemo(() => {
    const map = new Map<string, string>()
    for (const w of weldOptions) {
      if (w.weldNumber) map.set(w.weldId, w.weldNumber)
    }
    return map
  }, [weldOptions])

  const { trains, filterOptions } = useMemo(
    () => buildTrains(grouped, weldNumberByWeldId),
    [grouped, weldNumberByWeldId]
  )

  const [filterScopeIds, setFilterScopeIds] = useState<string[]>([])
  const [versionsKey, setVersionsKey] = useState<
    { scopeId: string; number: string } | null
  >(null)

  const visibleTrains = useMemo(
    () =>
      filterScopeIds.length === 0
        ? trains
        : trains.filter((t) => filterScopeIds.includes(t.scopeId)),
    [trains, filterScopeIds]
  )

  const scopeLabelById = useMemo(() => {
    const m = new Map<string, string>()
    for (const o of filterOptions) m.set(o.id, o.label)
    return m
  }, [filterOptions])

  const versionsTrain = useMemo(() => {
    if (!versionsKey) return null
    return (
      trains.find(
        (t) =>
          t.scopeId === versionsKey.scopeId && t.number === versionsKey.number
      ) ?? null
    )
  }, [trains, versionsKey])

  useEffect(() => {
    if (versionsKey && !versionsTrain) setVersionsKey(null)
  }, [versionsKey, versionsTrain])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <p className="text-sm font-semibold text-gray-700">Generated PQRs</p>
        </div>
        {filterOptions.length > 0 && (
          <div data-testid="pqr-history-filter" className="w-72">
            <MultiSelect
              values={filterScopeIds}
              options={filterOptions.map((o) => o.id)}
              onChange={setFilterScopeIds}
              placeholder="All welds"
              searchPlaceholder="Search welds…"
              ariaLabel="Filter by weld"
              getOptionLabel={(id) => scopeLabelById.get(id) ?? id}
              getTriggerLabel={(sel) =>
                sel.length === 1
                  ? scopeLabelById.get(sel[0]) ?? sel[0]
                  : `${sel.length} welds selected`
              }
            />
          </div>
        )}
      </div>

      {listLoading ? (
        <PageLoader
          compact
          label="Loading PQR history…"
          data-testid="pqr-history-loading"
        />
      ) : visibleTrains.length === 0 ? (
        <div className="rounded-xl border border-[#E2E5EA] bg-white p-5">
          <p className="text-xs text-gray-500">
            {filterScopeIds.length > 0
              ? 'No PQRs for the selected welds yet \u2014 clear the filter or click Generate PQR.'
              : importMode
                ? 'No imported PQR reports yet.'
                : 'No PQRs generated yet \u2014 click Generate PQR to start.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visibleTrains.map((t) => (
            <TrainCard
              key={`${t.scopeId}-${t.number}`}
              train={t}
              onDownload={onDownload}
              onRemove={onRemove}
              onPreview={onPreview}
              onShowVersions={(train) =>
                setVersionsKey({
                  scopeId: train.scopeId,
                  number: train.number
                })
              }
              busyId={busyId}
              importMode={importMode}
            />
          ))}
        </div>
      )}

      <ReportVersionsModal
        open={versionsTrain !== null}
        title={
          versionsTrain
            ? `All versions \u00B7 ${versionsTrain.scopeLabel} \u00B7 ${versionsTrain.number}`
            : ''
        }
        reports={
          versionsTrain
            ? [versionsTrain.downloadTarget, ...versionsTrain.older]
            : []
        }
        busyId={busyId}
        onDownload={onDownload}
        onRemove={onRemove}
        onClose={() => setVersionsKey(null)}
      />
    </div>
  )
}
